import React, { FC, memo, useMemo, useCallback } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useGetCurrencySymbol } from 'app/appState';
import { ArrivalStatisticsTotal } from 'entities/Warehouse';
import { roundNumber } from 'shared/utils/helpers/roundNumber';

interface ArrivalStatisticsCardProps {
  title: string;
  data: ArrivalStatisticsTotal;
  isDiff?: boolean;
}

export const ArrivalStatisticsCard: FC<ArrivalStatisticsCardProps> = memo((props) => {
  const { title, data, isDiff = false } = props;
  const { t } = useAppTranslation(['common', 'contracts']);
  const currencySymbol = useGetCurrencySymbol();

  const getValueColor = useCallback(
    (value: number): string => {
      if (isDiff) {
        if (isNaN(value)) {
          return 'text-primary';
        }
        return value >= 0 ? 'text-green-600' : 'text-red-600';
      }
      return 'text-primary';
    },
    [isDiff],
  );

  const rateDiffDisplay = useMemo(() => {
    if (isNaN(data.rateDiff)) {
      return {
        isNaN: true,
        value: null,
        sign: null,
        className: 'text-primary',
      };
    }
    const isPositive = data.rateDiff >= 0;
    return {
      isNaN: false,
      value: roundNumber(Math.abs(data.rateDiff)),
      sign: isPositive ? '+' : '-',
      className: isPositive ? 'text-green-600' : 'text-red-600',
    };
  }, [data.rateDiff]);

  const rateDiffPercentDisplay = useMemo(() => {
    if (isNaN(data.rateDiffPercent)) {
      return {
        isNaN: true,
        value: null,
        sign: null,
        className: 'text-primary',
      };
    }
    const isPositive = data.rateDiffPercent >= 0;
    return {
      isNaN: false,
      value: roundNumber(Math.abs(data.rateDiffPercent)),
      sign: isPositive ? '+' : '-',
      className: isPositive ? 'text-green-600' : 'text-red-600',
    };
  }, [data.rateDiffPercent]);

  return (
    <div className="bg-secondaryLight rounded-lg p-6 flex flex-col">
      <div className="font-semibold text-xl mb-6">{title}</div>

      <div className="bg-white rounded-lg p-6 space-y-4">
        <div className="flex justify-between items-center">
          <span className="text-primaryLight">{t('Contract rate')}</span>
          <span className={`font-semibold ${getValueColor(data.contractRate)}`}>
            {roundNumber(data.contractRate)} {currencySymbol}
          </span>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-primaryLight">{t('Current box rate')}</span>
          <span className={`font-semibold ${getValueColor(data.currentBoxRate)}`}>
            {roundNumber(data.currentBoxRate)} {currencySymbol}
          </span>
        </div>

        <div className="border-t border-secondaryAccent pt-4 space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-primaryLight">{t('Rate difference')}</span>
            <span className={`font-semibold ${rateDiffDisplay.className}`}>
              {rateDiffDisplay.isNaN ? (
                'N/A'
              ) : (
                <>
                  {rateDiffDisplay.sign}
                  {rateDiffDisplay.value} {currencySymbol}
                </>
              )}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-primaryLight">{t('Rate difference percent')}</span>
            <span className={`font-semibold ${rateDiffPercentDisplay.className}`}>
              {rateDiffPercentDisplay.isNaN ? (
                'N/A'
              ) : (
                <>
                  {rateDiffPercentDisplay.sign}
                  {rateDiffPercentDisplay.value}%
                </>
              )}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-primaryLight">{t('Personal contract rate amount')}</span>
            <span className={`font-semibold ${getValueColor(data.personalContractRateAmount)}`}>
              {roundNumber(data.personalContractRateAmount)} {currencySymbol}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-primaryLight">{t('Business contract rate amount')}</span>
            <span className={`font-semibold ${getValueColor(data.businessContractRateAmount)}`}>
              {roundNumber(data.businessContractRateAmount)} {currencySymbol}
            </span>
          </div>
        </div>

        <div className="border-t border-secondaryAccent pt-4 space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-primaryLight">{t('Square amount')}</span>
            <span className={`font-semibold ${getValueColor(data.squareAmount)}`}>{roundNumber(data.squareAmount)} m²</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-primaryLight">{t('Personal square amount')}</span>
            <span className={`font-semibold ${getValueColor(data.personalSquareAmount)}`}>{roundNumber(data.personalSquareAmount)} m²</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-primaryLight">{t('Business square amount')}</span>
            <span className={`font-semibold ${getValueColor(data.businessSquareAmount)}`}>{roundNumber(data.businessSquareAmount)} m²</span>
          </div>
        </div>

        <div className="border-t border-secondaryAccent pt-4 space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-primaryLight">{t('Box amount')}</span>
            <span className={`font-semibold ${getValueColor(data.boxAmount)}`}>{roundNumber(data.boxAmount)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-primaryLight">{t('Personal amount')}</span>
            <span className={`font-semibold ${getValueColor(data.personalAmount)}`}>{roundNumber(data.personalAmount)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-primaryLight">{t('Business amount')}</span>
            <span className={`font-semibold ${getValueColor(data.businessAmount)}`}>{roundNumber(data.businessAmount)}</span>
          </div>
        </div>
      </div>
    </div>
  );
});
